import { useEffect } from "react";
import { Phone, Check, ArrowRight } from "lucide-react";
import { serviceBySlug, servicePath, SERVICES_PAGES } from "../lib/services";
import { CITIES, cityPath } from "../lib/cities";
import SeoPageShell, { PHONE, PHONE_HREF } from "../components/SeoPageShell";

function setMeta(svc) {
  document.title = svc.metaTitle;
  document.querySelector('meta[name="description"]')?.setAttribute("content", svc.metaDesc);
  document.querySelector('link[rel="canonical"]')?.setAttribute("href", `https://usemilla.com${servicePath(svc)}`);
  const ld = document.createElement("script");
  ld.type = "application/ld+json";
  ld.id = "service-jsonld";
  ld.textContent = JSON.stringify([
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: svc.label,
      serviceType: svc.label,
      provider: { "@id": "https://usemilla.com/#business", "@type": "LocalBusiness", name: "milla", telephone: "+16177179852", url: "https://usemilla.com/" },
      areaServed: [{ "@type": "State", name: "Massachusetts" }, { "@type": "State", name: "New Hampshire" }],
      url: `https://usemilla.com${servicePath(svc)}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: svc.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ]);
  document.head.appendChild(ld);
  return () => document.getElementById("service-jsonld")?.remove();
}

export default function ServicePage() {
  const slug = window.location.pathname.replace("/services/", "").replace(/\/$/, "");
  const svc = serviceBySlug(slug);

  useEffect(() => {
    if (svc) return setMeta(svc);
    window.location.replace("/");
  }, [svc]);

  if (!svc) return null;
  const otherServices = SERVICES_PAGES.filter((s) => s.slug !== svc.slug);

  return (
    <SeoPageShell testid="service-page">
      {(onStart) => (
        <main className="mx-auto max-w-7xl px-5 sm:px-8">
          <section className="pt-16 pb-12 sm:pt-24 sm:pb-16">
            <p className="inline-flex items-center gap-2 rounded-full bg-lavender px-4 py-1.5 text-sm font-semibold text-violet">
              Serving Massachusetts &amp; New Hampshire
            </p>
            <h1 className="mt-6 max-w-3xl font-display text-4xl leading-[1.05] text-charcoal sm:text-5xl lg:text-6xl" data-testid="service-h1">
              {svc.h1}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-charcoal/60">{svc.intro}</p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button onClick={onStart} data-testid="service-hero-cta" className="inline-flex items-center gap-2 rounded-full bg-violet px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-violet-deep hover:-translate-y-0.5">
                Get an instant photo quote <ArrowRight size={18} />
              </button>
              <a href={PHONE_HREF} className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-semibold text-charcoal ring-1 ring-charcoal/10 transition-colors hover:bg-lavender hover:text-violet">
                <Phone size={18} /> {PHONE}
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-charcoal/55">
              {["Licensed & insured", "Firm quotes from a photo", "Pickups from $249", "Donation-first"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><Check size={15} className="text-violet" /> {t}</span>
              ))}
            </div>
          </section>

          <section className="pb-16">
            <h2 className="font-display text-lg text-charcoal">What we take</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {svc.items.map((i) => (
                <div key={i} className="flex items-center gap-2.5 rounded-2xl bg-white/70 px-4 py-3 ring-1 ring-charcoal/5">
                  <Check size={16} className="shrink-0 text-violet" />
                  <span className="text-sm font-medium text-charcoal/75">{i}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="pb-16">
            <h2 className="font-display text-lg text-charcoal">Common questions</h2>
            <div className="mt-5 grid gap-5 lg:grid-cols-3">
              {svc.faqs.map((f) => (
                <div key={f.q} className="rounded-[2rem] bg-white/70 p-7 ring-1 ring-charcoal/5">
                  <h3 className="font-semibold text-charcoal">{f.q}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/60">{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="pb-16">
            <div className="rounded-[2.5rem] bg-charcoal px-8 py-12 text-canvas sm:px-14">
              <h2 className="font-display text-2xl text-white sm:text-3xl">One photo. One firm price. Gone.</h2>
              <p className="mt-3 max-w-xl text-canvas/60">Snap a photo of what needs to go and get a bookable quote in under a minute — no on-site estimates, no surprises.</p>
              <button onClick={onStart} data-testid="service-bottom-cta" className="mt-7 inline-flex items-center gap-2 rounded-full bg-violet px-8 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-violet-deep">
                Get an instant quote <ArrowRight size={16} />
              </button>
            </div>
          </section>

          <section className="pb-10">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-charcoal/40">{svc.label} by town</h2>
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
              {CITIES.map((c) => (
                <a key={c.slug} href={cityPath(c)} className="text-sm text-charcoal/60 underline-offset-4 transition-colors hover:text-violet hover:underline">
                  {svc.cityAnchor} {c.name}, {c.abbr}
                </a>
              ))}
            </div>
          </section>

          <section className="pb-16">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-charcoal/40">Other services</h2>
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
              {otherServices.map((s) => (
                <a key={s.slug} href={servicePath(s)} className="text-sm text-charcoal/60 underline-offset-4 transition-colors hover:text-violet hover:underline">{s.label}</a>
              ))}
              <a href="/junk-removal-cost" className="text-sm text-charcoal/60 underline-offset-4 transition-colors hover:text-violet hover:underline">Junk removal cost guide</a>
              <a href="/" className="text-sm font-semibold text-violet underline-offset-4 hover:underline">Home →</a>
            </div>
          </section>
        </main>
      )}
    </SeoPageShell>
  );
}
